import React, { useEffect, useState } from 'react';
import { SimpalsidProvider } from '@simpals/next-simpalsbar/dist/react';
import { setGlobalDevMode } from '@simpals/next-simpalsbar/dist/helpers/globalMode';
import { PROJECT_ID, IS_STAGING, CookieName } from '../../constants';
import { parseCookie } from '../../utils/cookie';

// IMPORTANT: Set global dev mode IMMEDIATELY at module level
// This ensures all simpalsbar methods (like fetchMyAccount) use the correct URL
// Must be called before any simpalsbar functions are invoked
if (typeof window !== 'undefined') {
  setGlobalDevMode(IS_STAGING);
}

// Import fetchMyAccount from the library
const fetchMyAccountFromLib = async () => {
  if (typeof window === 'undefined') return null;

  try {
    // Dynamically import to access the library's fetchMyAccount
    const { fetchMyAccount } = await import(
      '@simpals/next-simpalsbar/dist/methods/fetchAccount.js'
    );
    return await fetchMyAccount();
  } catch (error) {
    console.error('Error fetching account:', error);
    return null;
  }
};

function SimpalsBarProvider({ children }) {
  const [isReady, setIsReady] = useState(false);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      // Handle token from URL after redirect
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const sessionKey = urlParams.get('sessionKey');
      const auth = urlParams.get('auth');

      if (token || sessionKey || auth) {
        // Set cookies
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        const expiresStr = expires.toUTCString();

        if (token) {
          document.cookie = `${CookieName.SimpalsIdAuth2}=${token}; path=/; expires=${expiresStr}; SameSite=Lax`;
        }
        if (sessionKey) {
          document.cookie = `${CookieName.SessionKey}=${sessionKey}; path=/; expires=${expiresStr}; SameSite=Lax`;
        }
        if (auth) {
          document.cookie = `${CookieName.SimpalsIdAuth}=${auth}; path=/; expires=${expiresStr}; SameSite=Lax`;
        }

        // Remove token params from URL
        urlParams.delete('token');
        urlParams.delete('sessionKey');
        urlParams.delete('auth');

        const newUrl = urlParams.toString()
          ? `${window.location.pathname}?${urlParams.toString()}`
          : window.location.pathname;

        window.history.replaceState({}, '', newUrl);

        // Reload to apply new cookies
        window.location.reload();
      } else {
        // Check if we have auth cookies and fetch account
        const cookies = parseCookie();
        const hasAuthToken =
          cookies[CookieName.SimpalsIdAuth2] || cookies[CookieName.SimpalsIdAuth];

        if (hasAuthToken) {
          const accountData = await fetchMyAccountFromLib();
          setAccount(accountData);
        }

        setIsReady(true);
      }
    };

    initializeAuth();
  }, []);

  if (!isReady) {
    return null; // or a loading spinner
  }

  return (
    <SimpalsidProvider
      projectId={PROJECT_ID}
      isDev={IS_STAGING}
      siteUrl={`${window.location.origin}/sid`}
      account={account}
    >
      {children}
    </SimpalsidProvider>
  );
}

export default SimpalsBarProvider;
